/**
 * Device-local memory of the listing that sent the visitor into the auth flow
 * (the "Inloggen voor profiel" / contact gate on property pages). The auth
 * pages read it to replace their generic heading with the property the user
 * was looking at. sessionStorage on purpose: the login/register hop stays in
 * the same tab, and the actual post-auth destination travels via the returnTo
 * query param — this is presentation-only enhancement, never routing state.
 * A missing or stale entry must always degrade to the generic auth page.
 */

export const AUTH_CONTEXT_STORAGE_KEY = 'hm_auth_context_v1';
export const AUTH_CONTEXT_TTL_MS = 30 * 60 * 1000;

export interface AuthContextEntry {
  propertyId: string;
  title: string;
  /** Frontend-local canonical property path (matches the CTA's returnTo). */
  path: string;
  formattedPrice?: string;
  imageUrl?: string;
  locale: string;
  storedAt: number;
}

export type AuthContextInput = Omit<AuthContextEntry, 'storedAt'>;

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeEntry(value: unknown, now: number): AuthContextEntry | null {
  if (!isPlainObject(value)) return null;

  if (
    typeof value.propertyId !== 'string' || !value.propertyId.trim()
    || typeof value.title !== 'string' || !value.title.trim()
    || typeof value.path !== 'string' || !value.path.startsWith('/')
    || typeof value.locale !== 'string'
    || typeof value.storedAt !== 'number'
  ) {
    return null;
  }

  if (value.storedAt > now || now - value.storedAt > AUTH_CONTEXT_TTL_MS) return null;

  return {
    propertyId: value.propertyId,
    title: value.title,
    path: value.path,
    formattedPrice: typeof value.formattedPrice === 'string' ? value.formattedPrice : undefined,
    imageUrl: typeof value.imageUrl === 'string' ? value.imageUrl : undefined,
    locale: value.locale,
    storedAt: value.storedAt,
  };
}

export function rememberAuthContext(input: AuthContextInput) {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(
      AUTH_CONTEXT_STORAGE_KEY,
      JSON.stringify({ ...input, storedAt: Date.now() } satisfies AuthContextEntry),
    );
  } catch {
    // Quota/serialization failures must never affect UX.
  }
}

export function readAuthContext(): AuthContextEntry | null {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const stored = storage.getItem(AUTH_CONTEXT_STORAGE_KEY);
    if (!stored) return null;
    return normalizeEntry(JSON.parse(stored), Date.now());
  } catch {
    return null;
  }
}

